import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  FormControl,
  TextField,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  Box,
  Stack,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import {
  addAccount,
  updateAccount,
  deleteAccount,
} from '../../redux/financeAccountSlice';

const FormContainer = styled(Paper)(({ theme }) => ({
  maxWidth: '800px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(3),
  margin: 0,
  borderRadius: '20px',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
  },
}));

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fafafa',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgb(0,98,105)',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgb(0,98,105)',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    '&.Mui-focused': {
      color: 'rgb(0,98,105)',
      fontWeight: 600,
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: '12px',
  backgroundColor: '#fafafa',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgb(0,98,105)',
  },
  '&.Mui-focused': {
    backgroundColor: '#ffffff',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgb(0,98,105)',
    borderWidth: '2px',
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  minHeight: '48px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const PrimaryButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: 'rgb(0,98,105)',
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgb(0,85,91)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 98, 105, 0.4)',
  },
}));

const SecondaryButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
  },
}));

const DeleteButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(255, 0, 0, 0.25)',
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  color: '#1a1a1a',
  marginBottom: theme.spacing(4),
  textAlign: 'left',
  position: 'relative',
  paddingBottom: theme.spacing(2),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '60px',
    height: '4px',
    backgroundColor: 'rgb(0,98,105)',
    borderRadius: '2px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
    marginBottom: theme.spacing(3),
  },
}));

const fillItem = (items, params, currentUser) => {
  let item = {
    id: 0,
    priority: 1,
    name: '',
    userId: currentUser.id,
    active: true,
  };
  let id = parseInt(params.id);
  if (!!id) {
    const itemFound = items.find((item) => item.id === id);
    if (!!itemFound) {
      item = itemFound;
    } else {
      id = 0;
    }
  }
  return item;
};

const goToList = (navigate) => {
  navigate('/accounts');
};

/**
 * Validates account data
 * @param {Object} item - Account data to validate
 * @returns {Object} Validation errors object with field names as keys
 */
const validateAccount = (item) => {
  const errors = {};

  // Check name is not empty
  if (!item.name || !item.name.toString().trim().length) {
    errors.name = 'Nome é obrigatório';
  }

  // Check priority is valid
  if (!item.priority || item.priority <= 0) {
    errors.priority = 'Prioridade deve ser maior que 0';
  }

  return errors;
};

const Edit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [errors, setErrors] = useState({});

  const update = async (account) => {
    await dispatch(updateAccount(account)).unwrap();
  };

  const add = async (account) => {
    await dispatch(addAccount(account)).unwrap();
  };

  const deleteAccountAction = async (id) => {
    await dispatch(deleteAccount(id)).unwrap();
  };

  const submitForm = async (event) => {
    event.preventDefault();
    
    // Validate form data
    const validationErrors = validateAccount(item);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      console.error('Validation errors:', validationErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});

    try {
      if (item.id) {
        await update(item);
      } else {
        await add(item);
      }
      navigate('/accounts');
    } catch (error) {}
  };

  const items = useSelector((state) => state.financeAccount.items);
  const users = useSelector((state) => state.account.users);
  const currentUser = useSelector((state) => state.account.user);

  let initialData = fillItem(items, params, currentUser);
  const [item, updateItem] = useState({ ...initialData });

  const handleUpdateItem = (updates) => {
    updateItem({ ...item, ...updates });
    // Clear errors for updated fields
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      Object.keys(updates).forEach((key) => {
        delete newErrors[key];
      });
      return newErrors;
    });
  };

  if (initialData.id !== item.id) {
    updateItem({ ...initialData });
  }

  const userOptions = (users || []).map((user) => (
    <MenuItem key={user.id} value={user.id}>{`${user.name}`}</MenuItem>
  ));

  const optionalButton = !!item.id ? (
    <DeleteButton
      type="button"
      variant="contained"
      startIcon={<DeleteIcon />}
      onClick={async (event) => {
        try {
          await deleteAccountAction(item.id);
          navigate('/accounts');
        } catch (error) {}
      }}
    >
      Excluir
    </DeleteButton>
  ) : null;

  return (
    <Box sx={{ 
      padding: { xs: 0.5, sm: 1 },
      minHeight: '100vh',
    }}>
      <FormContainer elevation={0}>
        <PageTitle component="h1" variant="h4">
          Conta Financeira
        </PageTitle>

        <StyledForm onSubmit={(event) => submitForm(event)}>
          <input type="hidden" name="id" value={item.id} />

          <StyledFormControl>
            <StyledTextField
              id="name"
              name="name"
              label="Nome"
              variant="outlined"
              fullWidth
              autoFocus
              value={item.name}
              onChange={(event) =>
                handleUpdateItem({ name: event.target.value })
              }
              error={!!errors.name}
              helperText={errors.name}
              placeholder="Ex: Conta Corrente, Poupança, Carteira..."
            />
          </StyledFormControl>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <StyledFormControl sx={{ flex: 1 }}>
              <StyledTextField
                id="priority"
                name="priority"
                label="Prioridade"
                type="number"
                variant="outlined"
                fullWidth
                inputProps={{ step: 1, min: 1 }}
                value={item.priority}
                onChange={(event) =>
                  handleUpdateItem({ priority: event.target.value })
                }
                error={!!errors.priority}
                helperText={errors.priority}
                placeholder="1"
              />
            </StyledFormControl>

            <StyledFormControl sx={{ flex: 1 }}>
              <InputLabel id="userId-label">Responsável</InputLabel>
              <StyledSelect
                labelId="userId-label"
                id="userID"
                value={item.userId ? item.userId : ''}
                label="Responsável"
                onChange={(event) =>
                  handleUpdateItem({ userId: event.target.value })
                }
              >
                {userOptions}
              </StyledSelect>
            </StyledFormControl>
          </Box>

          <StyledFormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={!!item.active ? item.active : false}
                  onChange={(event) =>
                    handleUpdateItem({ active: event.target.checked })
                  }
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'rgb(0,98,105)',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'rgb(0,98,105)',
                    },
                  }}
                />
              }
              label="Ativo"
            />
          </StyledFormControl>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ pt: 3, mt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}
          >
            <PrimaryButton
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ flex: 1 }}
            >
              Salvar
            </PrimaryButton>

            {optionalButton}

            <SecondaryButton
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => goToList(navigate)}
            >
              Voltar
            </SecondaryButton>
          </Stack>
        </StyledForm>
      </FormContainer>
    </Box>
  );
};

export default Edit;
